// Server-assigned identity fields are not part of the content a user edits, and
// menu rebuilds routinely drop them (the row-lock menu rebuilds rule objects
// without their `publicId`). Comparing them would flag a phantom change the
// moment a menu is opened, so they're excluded from change-detection by default.
// A caller that genuinely wants to compare identity can pass an empty set.
export const IDENTITY_KEYS: ReadonlySet<string> = new Set(['publicId'])

// Canonicalise a single field value so that "empty" reads as empty regardless
// of the exact shape it arrives in: undefined / null / '' / [] / false all
// collapse to the same token. This is what lets a rule whose optional field was
// stripped by the backend (e.g. an `all_values` row-lock has no `value`) compare
// equal to a rebuilt rule that carries the default `value: ''` / `[]`.
const canonical = (value: unknown): string => {
  if (value === undefined || value === null || value === false || value === '') return ''
  if (Array.isArray(value)) return value.length === 0 ? '' : JSON.stringify(value)
  return String(value)
}

// Value-based equality for flat "record" objects (colour rules, row-lock rules…).
// It compares the UNION of both objects' keys, so any attribute added to the
// underlying type is picked up automatically — there is no field list to keep in
// sync, which is what a hand-written per-field comparison gets wrong silently.
// Keys in `ignoreKeys` (identity fields by default) are skipped.
//
// Deliberately looser than a strict deep-equal, matching what change-detection
// needs: key order is ignored, and empty variants (undefined / '' / [] / false)
// are treated as equal. It is shallow (no recursion into nested objects) and
// coerces primitives via String(), so it suits the flat rule objects here —
// values are strings, booleans, and string arrays.
export const recordsEqual = (
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  ignoreKeys: ReadonlySet<string> = IDENTITY_KEYS
): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if (ignoreKeys.has(key)) continue
    if (canonical(a[key]) !== canonical(b[key])) return false
  }
  return true
}

// Compare two arrays of record objects positionally (order-sensitive), element
// by element via recordsEqual.
export const recordListsEqual = (
  a: Record<string, unknown>[],
  b: Record<string, unknown>[],
  ignoreKeys: ReadonlySet<string> = IDENTITY_KEYS
): boolean => {
  if (a.length !== b.length) return false
  return a.every((item, index) => recordsEqual(item, b[index], ignoreKeys))
}
